
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoading } from '@/contexts/LoadingContext';

type PageLoadingKey = 
  | 'page-bible-init'
  | 'page-bible-search'
  | 'page-prayers-load'
  | 'page-prayers-refresh'
  | 'page-profile-load'
  | `page-${string}-load`
  | `page-transition-${string}-${string}`;

export const usePageLoading = () => {
  const location = useLocation();
  const { setLoading, clearLoading, isLoading, getLoadingMessage } = useLoading();

  // Set page loading state
  const setPageLoading = useCallback((
    key: PageLoadingKey,
    loading: boolean,
    message?: string,
    progress?: number
  ) => {
    setLoading(key, loading, message || 'Loading page...', 'page', progress);
  }, [setLoading]);

  // Clear page loading state
  const clearPageLoading = useCallback((key: PageLoadingKey) => {
    clearLoading(key);
  }, [clearLoading]);

  // Check if any page loading is active
  const isPageLoading = useCallback((key?: PageLoadingKey) => {
    if (key) {
      return isLoading(key);
    }
    return false;
  }, [isLoading]);

  // Get page loading message
  const getPageLoadingMessage = useCallback((key: PageLoadingKey) => {
    return getLoadingMessage(key);
  }, [getLoadingMessage]);

  // Auto-clear page loading states when route changes
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Clear all page-specific loading states when route changes
    const pageLoadingKeys = [
      'page-bible-init',
      'page-bible-search',
      'page-prayers-load',
      'page-prayers-refresh',
      'page-profile-load'
    ] as const;

    pageLoadingKeys.forEach(key => {
      clearPageLoading(key);
    });

    // Set a brief transition loading state
    const transitionKey = `page-transition-to-${currentPath.replace('/', '')}` as PageLoadingKey;
    setPageLoading(transitionKey, true, 'Loading page...', 0);
    
    // Clear transition loading after a brief delay
    const timer = setTimeout(() => {
      clearPageLoading(transitionKey);
    }, 300);

    return () => {
      clearTimeout(timer);
      clearPageLoading(transitionKey);
    };
  }, [location.pathname, setPageLoading, clearPageLoading]);

  return {
    setPageLoading,
    clearPageLoading,
    isPageLoading,
    getPageLoadingMessage,
    currentPath: location.pathname
  };
};
